import {
  useId,
  useMemo,
  useState,
  type ComponentProps,
  type ReactNode,
} from 'react';
import {
  Bleed,
  Box,
  IconClear,
  IconTick,
  Stack,
  Tab,
  TabPanel,
  TabPanels,
  Tabs,
  TabsProvider,
} from 'braid-design-system';
import { CodeContainer } from 'scoobie';
import { ClientOnly } from '#src/components/ClientOnly.tsx';
import { ThemedExample } from '#src/components/ThemedExample/ThemedExample.tsx';

type Status = {
  label: string;
  success?: boolean;
  children?: Status[];
};

const APPLICATION_HIERARCHY: Status[] = [
  {
    label: 'New',
  },
  {
    label: 'Review',
    children: [
      {
        label: 'Shortlist',
      },
      {
        label: 'Phone screen',
      },
    ],
  },
  {
    label: 'Interview',
    children: [
      {
        label: 'First interview',
      },
      {
        label: 'Case study',
      },
      {
        label: 'Final interview',
      },
    ],
  },
  {
    label: 'Offer',
    children: [
      {
        label: 'Offer sent',
      },
      {
        label: 'Offer accepted',
      },
      {
        label: 'References',
      },
      {
        label: 'Psychometrics',
      },
    ],
  },
  {
    label: 'Hired',
    success: true,
  },
  {
    label: 'Talent pool',
    success: false,
  },
  {
    label: 'Withdrawn',
    children: [
      {
        label: 'Offer declined',
        success: false,
      },
      {
        label: 'Accepted another offer',
        success: false,
      },
    ],
  },
];

const APPLICATION_FLAT = APPLICATION_HIERARCHY.flatMap((phase) =>
  phase.children ? phase.children : phase,
);

const APPLICATION_PHASES = Object.fromEntries(
  APPLICATION_HIERARCHY.map(({ label }, index) => [label, index]),
);

const APPLICATION_STATUSES = Object.fromEntries(
  APPLICATION_FLAT.map(({ label }, index) => [label, index] as const),
);

const TAB_ICONS = {
  undefined,
  true: <IconTick />,
  false: <IconClear />,
};

const ProcessCard = ({ children }: { children: ReactNode }) => (
  <Box
    background="surface"
    borderRadius="large"
    boxShadow="borderNeutralLight"
    paddingX="gutter"
    paddingY="small"
  >
    {children}
  </Box>
);

const ProcessTabs = ({
  bleed,
  children,
  hideIcon,
  items,
  label,
  onChange,
  selectedItem,
}: {
  bleed: 'none' | 'small';
  children?: ComponentProps<typeof Stack>['children'];
  hideIcon: boolean;
  items: Status[];
  label: string;
  onChange: ComponentProps<typeof TabsProvider>['onChange'];
  selectedItem?: ComponentProps<typeof TabsProvider>['selectedItem'];
}) => {
  const id = useId();

  return (
    <TabsProvider id={id} onChange={onChange} selectedItem={selectedItem}>
      <Stack space="small">
        <Bleed bottom={bleed}>
          <Tabs align="center" divider="full" label={label} size="small">
            {items.map((item, index) => (
              <Tab
                icon={hideIcon ? undefined : TAB_ICONS[`${item.success}`]}
                item={item.label}
                key={index}
              >
                {item.label}
              </Tab>
            ))}
          </Tabs>
        </Bleed>

        {children}
      </Stack>
    </TabsProvider>
  );
};

export const FlatApplicationModel = ({
  children,
}: {
  children: ComponentProps<typeof Stack>['children'];
}) => {
  const [statusIndex, setStatusIndex] = useState(3);

  const timestamp = useMemo(
    () => new Date().toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [statusIndex],
  );

  const status = APPLICATION_FLAT[statusIndex];

  return (
    <Stack space="medium">
      <ThemedExample>
        <ProcessCard>
          <ProcessTabs
            bleed="small"
            hideIcon={false}
            items={APPLICATION_FLAT}
            onChange={setStatusIndex}
            selectedItem={status.label}
            label="Statuses"
          />
        </ProcessCard>
      </ThemedExample>

      {children}

      <ClientOnly>
        <CodeContainer
          code={JSON.stringify(
            {
              status: {
                label: status.label,
                index: APPLICATION_STATUSES[status.label] + 1,
                total: APPLICATION_FLAT.length,
              },
              success: status.success ?? null,
              timestamp,
            },
            null,
            2,
          )}
          language="json"
        />
      </ClientOnly>
    </Stack>
  );
};

export const HierarchicalApplicationModel = ({
  children,
}: {
  children: ComponentProps<typeof Stack>['children'];
}) => {
  const [phaseIndex, setPhaseIndex] = useState(2);
  const [statusIndex, setStatusIndex] = useState(0);

  const timestamp = useMemo(
    () => new Date().toISOString(),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [phaseIndex, statusIndex],
  );

  const phase = APPLICATION_HIERARCHY[phaseIndex];

  const status = phase.children?.[statusIndex] ?? phase;

  return (
    <Stack space="medium">
      <ThemedExample>
        <ProcessCard>
          <ProcessTabs
            bleed="none"
            hideIcon={true}
            items={APPLICATION_HIERARCHY}
            label="Phases"
            onChange={(index) => {
              setPhaseIndex(index);
              setStatusIndex(0);
            }}
            selectedItem={phase.label}
          >
            <TabPanels>
              {APPLICATION_HIERARCHY.map((item, index) => (
                <TabPanel item={item.label} key={index}>
                  <ProcessTabs
                    bleed="small"
                    hideIcon={false}
                    items={item.children?.length ? item.children : [phase]}
                    label="Statuses"
                    onChange={setStatusIndex}
                  />
                </TabPanel>
              ))}
            </TabPanels>
          </ProcessTabs>
        </ProcessCard>
      </ThemedExample>

      {children}

      <ClientOnly>
        <CodeContainer
          code={JSON.stringify(
            {
              phase: {
                label: phase.label,
                index: APPLICATION_PHASES[phase.label] + 1,
                total: APPLICATION_HIERARCHY.length,
              },
              status: {
                label: status.label,
                index: APPLICATION_STATUSES[status.label] + 1,
                total: APPLICATION_FLAT.length,
              },
              success: status.success ?? null,
              timestamp,
            },
            null,
            2,
          )}
          language="json"
        />
      </ClientOnly>
    </Stack>
  );
};
